import React, { useState, useEffect, useRef } from "react";
import { getAssetUrl } from "../../config/api";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Avatar,
  Button,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import { EXCHANGE_CODES } from "../../websocket/constants";
import octopusInstance from "../../services/octopus/octopusInstance";
import { getPinnedInstrumentsAction } from "../../store/actions/watchlist";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

// Try to import the logo
// let tradehLogo;
// try {
//   tradehLogo = require("../../images/tradeh-logo.png");
// } catch (e) {
//   console.warn("Logo file not found in src/images/tradeh-logo.png");
// }

// let tradexLogo = "/assets/AppLogo.png";

const AppBar = ({
  onNavigate,
  onLogout,
  position,
  handleChartOpen,
  activeComponent,
}) => {
  let tradexLogo = getAssetUrl("AppLogo.png");
  const pinnedInstruments = useSelector((state) => state.marketwatch.pins);
  const { userData } = useSelector((state) => state.profile);
  const [isVisible, setIsVisible] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const subscribedRef = useRef(new Set());
  const dispatch = useDispatch();
  const [dynamicInfo, setDynamicInfo] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
      if (document.visibilityState === "visible") {
        // Refresh data when returning to tab
        dispatch(getPinnedInstrumentsAction());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  // Fetch initial pinned instruments
  useEffect(() => {
    dispatch(getPinnedInstrumentsAction());
  }, [dispatch]);

  useEffect(() => {
    if (!isVisible) return; // Don't subscribe when tab is hidden
    const handlers = [];
    pinnedInstruments.forEach((index) => {
      const subscriptionLocation = `pinnedInstrument-${index.token}`;
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation,
        payload: {
          exchangeCode: EXCHANGE_CODES[index.exchange],
          instrumentToken: index.token,
        },
      });

      handler.subscribe(({ msg }) => {
        setDynamicInfo((prev) => ({
          ...prev,
          [index.token]: {
            ...msg,
            selectedClass:
              prev[index.token]?.ltp < msg.ltp
                ? "buy"
                : prev[index.token]?.ltp > msg.ltp
                ? "sell"
                : "",
          },
        }));
      });

      handlers.push(handler);
    });
    return () => {
      handlers.forEach((handler) => handler.unsubscribe());
    };
  }, [pinnedInstruments, isVisible]);

  const menuItems = [
    {
      label: "Profile",
      action: () => {
        onNavigate("profile");
        setAnchorEl(null);
      },
      icon: <AccountCircleIcon fontSize="small" />,
    },
    {
      label: "Reports",
      action: () => {
        onNavigate("tradehistory");
        setAnchorEl(null);
      },
      icon: <HistoryIcon fontSize="small" />,
    },
    {
      label: "Logout",
      action: () => {
        localStorage.clear();
        window.location.href = "/login";
        setAnchorEl(null);
      },
      icon: <LogoutIcon fontSize="small" />,
    },
   
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const navigationItems = [
    { 
      label: "Dashboard", 
      icon: <DashboardIcon />, 
      action: () => onNavigate("dashboard") 
    },
    { 
      label: "Orders", 
      icon: <ReceiptIcon />, 
      action: () => onNavigate("orders") 
    },
    { 
      label: "Portfolio", 
      icon: <AccountBalanceWalletIcon />, 
      action: () => onNavigate("portfolio") 
    },
    { 
      label: "Funds", 
      icon: <PaymentsIcon />, 
      action: () => onNavigate("funds") 
    },
    { 
      label: "Support", 
      icon: <SupportAgentIcon />, 
      action: () => onNavigate("support") 
    }
  ];

  const drawerContent = (
    <Box
      sx={{
        width: 250,
        height: "100%",
        background: "linear-gradient(135deg, #0A1929 0%, #0F2942 100%)",
        color: "white",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "auto",
        "&::before": {
          content: '""',
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(79, 172, 254, 0.1) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0, 242, 254, 0.08) 0%, transparent 40%)",
          pointerEvents: "none",
          zIndex: 0,
        }
      }}
    >
      {/* Logo in drawer header */}
      <Box sx={{ 
        height: "5rem",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        marginTop: "1rem"
      }}>
        <img
          src={tradexLogo}
          alt="Tradex"
          style={{
            height: "48px",
            width: "48px"
          }}
        />
      </Box>
      
      {/* User Profile Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: "5rem",
            height: "5rem",
            background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            color: "white",
            fontSize: "2rem",
            border: "0.1875rem solid rgba(255, 255, 255, 0.2)",
            boxShadow: "0 0.25rem 1.25rem rgba(0,0,0,0.3)",
            mb: 2,
          }}
        >
          {userData?.name?.charAt(0).toUpperCase() || "U"}
        </Avatar>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "white",
            mb: 0.5,
          }}
        >
          {userData?.name || "User"}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            mb: 1,
          }}
        >
          {userData?.email || ""}
        </Typography>

        <Box
          sx={{
            display: "inline-block",
            px: 2,
            py: 0.5,
            borderRadius: "0.625rem",
            backgroundColor: "rgba(79, 172, 254, 0.15)",
            border: "0.0625rem solid rgba(79, 172, 254, 0.3)",
            mt: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#4facfe",
              fontWeight: 500,
            }}
          >
            Active
          </Typography>
        </Box>
      </Box>

      <Divider 
        sx={{ 
          my: 3, 
          borderColor: "rgba(255, 255, 255, 0.08)",
          width: "80%",
          mx: "auto",
        }} 
      />
      
      {/* Navigation Items */}
      <Box sx={{ flex: 1, px: 2 }}>
        <List>
          {navigationItems.map((item) => (
            <ListItem
              button
              key={item.label}
              onClick={() => {
                item.action();
                setDrawerOpen(false);
              }}
              sx={{
                py: 1.5,
                px: 2,
                color: "white",
                borderRadius: "0.125rem",
                mb: 1.5,
                backgroundColor: activeComponent === item.label.toLowerCase() 
                  ? "rgba(79, 172, 254, 0.15)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
                transition: "all 0.2s ease",
                position: "relative",
                overflow: "hidden",
                "&::after": activeComponent === item.label.toLowerCase() ? {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "20%",
                  height: "60%",
                  width: "0.1875rem",
                  backgroundColor: "#4facfe",
                  borderRadius: "0.25rem",
                } : {},
              }}
            >
              <ListItemIcon sx={{ 
                color: activeComponent === item.label.toLowerCase() 
                  ? "#4facfe" 
                  : "rgba(255, 255, 255, 0.7)", 
                minWidth: 40,
                transition: "color 0.2s ease",
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: { 
                    fontWeight: activeComponent === item.label.toLowerCase() ? 600 : 400,
                    transition: "font-weight 0.2s ease",
                    fontSize: "0.95rem",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Account Actions */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255, 255, 255, 0.5)",
            textTransform: "uppercase",
            fontWeight: 500,
            letterSpacing: 0.5,
            px: 2,
            mb: 1,
            display: "block",
          }}
        >
          Account
        </Typography>
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.label}
              onClick={() => {
                item.action();
                setDrawerOpen(false);
              }}
              sx={{
                py: 1.5,
                px: 2,
                color: "white",
                borderRadius: "0.125rem",
                mb: 1,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
                transition: "background-color 0.2s ease",
              }}
            >
              <ListItemIcon sx={{ 
                color: "rgba(255, 255, 255, 0.7)", 
                minWidth: 40 
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: { 
                    fontWeight: 400,
                    fontSize: "0.95rem",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          mt: "auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "rgba(255, 255, 255, 0.5)",
            fontWeight: 400,
          }}
        >
          TradeH Trading Platform{" "}
          <Box 
            component="span" 
            sx={{ 
              color: "#4facfe",
              fontWeight: 600,
            }}
          >
            • v1.0.0
          </Box>
        </Typography>
      </Box>
    </Box>
  );

  const PinnedInstruments = () => (
    <Box
      sx={{
        display: "flex",
        gap: isMobile ? 0.5 : 1.5,
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { display: "none" },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        alignItems: "center",
        mr: 2,
        width: "100%",
      }}
    >
      {pinnedInstruments.map((index) => {
        const data = dynamicInfo[index.token] || {};
        const ltp = parseFloat(data.ltp);
        const absoluteChange = parseFloat(data.absoluteChange);
        const percentChange = parseFloat(data.percentChange);

        return (
          <Paper
            key={index.name}
            elevation={0}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              padding: isMobile ? "0.25rem 0.375rem" : "0.375rem 0.75rem",
              borderRadius: "0.25rem",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                transform: "translateY(-0.0625rem)",
              },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minWidth: isMobile ? "6.875rem" : "11.25rem",
              maxWidth: isMobile ? "8.125rem" : "13.75rem",
              height: isMobile ? "2.625rem" : "3.25rem",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    fontSize: isMobile ? "0.75rem" : "0.875rem",
                    color:
                      data.selectedClass === "buy"
                        ? "#00E676"
                        : data.selectedClass === "sell"
                        ? "#FF1744"
                        : "white",
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  {index.display_name.toUpperCase()}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                    fontSize: isMobile ? "0.7rem" : "0.8rem",
                    lineHeight: 1.2,
                  }}
                >
                  {!isNaN(ltp) ? ltp.toFixed(2) : "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  color:
                    data.selectedClass === "buy"
                      ? "#00E676"
                      : data.selectedClass === "sell"
                      ? "#FF1744"
                      : "white",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: "bold",
                    fontSize: isMobile ? "0.65rem" : "0.75rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {!isNaN(absoluteChange) ? absoluteChange.toFixed(2) : "-"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: isMobile ? "0.6rem" : "0.7rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {!isNaN(percentChange)
                    ? `(${percentChange.toFixed(2)}%)`
                    : "-"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  return (
    <>
      <MuiAppBar
        position="fixed"
        sx={{
          background: "linear-gradient(90deg, #0A1929 0%, #132f4c 100%)",
          color: "white",
          boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.15)",
          height: isMobile ? "3.5rem" : "4rem",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            gap: isMobile ? 1 : 0,
            p: isMobile ? "0 0.75rem" : "0 1.5rem",
            minHeight: isMobile ? "3.5rem !important" : "4rem !important",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              overflow: "hidden",
            }}
          >
            {!isMobile && (
              <img
                src={tradexLogo}
                alt="Tradex"
                style={{
                  height: "42px",
                  width: "42px",
                  marginRight: "24px"
                }}
              />
            )}
            <PinnedInstruments />

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  marginLeft: "auto",
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "0.75rem",
                  padding: "0.25rem",
                  boxShadow: "0 0.125rem 0.5rem rgba(0,0,0,0.1)",
                  minWidth: "fit-content",
                }}
              >
                {navigationItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={item.action}
                    startIcon={item.icon}
                    variant={activeComponent === item.label.toLowerCase() ? "contained" : "text"}
                    sx={{
                      mx: 0.5,
                      textTransform: "none",
                      color: activeComponent === item.label.toLowerCase() 
                        ? "#ffffff"
                        : "#ffffff",
                      backgroundColor: activeComponent === item.label.toLowerCase()
                        ? "rgba(79, 172, 254, 0.3)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: activeComponent === item.label.toLowerCase()
                          ? "rgba(79, 172, 254, 0.4)"
                          : "rgba(255, 255, 255, 0.1)",
                      },
                      borderRadius: "0.5rem",
                      px: 2,
                      py: 0.75,
                      fontSize: "0.9rem",
                      fontWeight: activeComponent === item.label.toLowerCase() ? 600 : 400,
                      minWidth: "auto",
                      transition: "all 0.2s ease",
                      position: "relative",
                      "&::after": activeComponent === item.label.toLowerCase() ? {
                        content: '""',
                        position: "absolute",
                        bottom: "0.25rem",
                        left: "20%",
                        width: "60%",
                        height: "0.125rem",
                        backgroundColor: "#4facfe",
                        borderRadius: "0.25rem",
                      } : {},
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={(e) => {
                if (isMobile) {
                  handleDrawerToggle();
                } else {
                  setAnchorEl(e.currentTarget);
                }
              }}
              sx={{
                padding: 0,
                ml: 2,
                "&:hover": { 
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {userData?.name ? (
                <Avatar
                  sx={{
                    width: "2.25rem",
                    height: "2.25rem",
                    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    border: "0.125rem solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "rgba(255, 255, 255, 0.4)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {userData?.name?.charAt(0).toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircleIcon sx={{ fontSize: "2.25rem", color: "white" }} />
              )}
            </IconButton>
          </Box>
        </Toolbar>
      </MuiAppBar>

      {!isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            elevation: 4,
            sx: {
              minWidth: "13.75rem",
              mt: 1,
              borderRadius: "0.75rem",
              background: "linear-gradient(135deg, #0A1929 0%, #0F2942 100%)",
              overflow: "hidden",
              border: "0.0625rem solid rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Box sx={{ 
            p: 2, 
            borderBottom: "0.0625rem solid rgba(255, 255, 255, 0.1)",
            background: "rgba(79, 172, 254, 0.1)",
          }}>
            <Typography variant="subtitle1" sx={{ color: "white", fontWeight: 600 }}>
              {userData?.name || "User"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
              {userData?.email || ""}
            </Typography>
          </Box>
          
          {menuItems.map((item) => (
            <MenuItem
              key={item.label}
              onClick={item.action}
              sx={{
                color: "white",
                py: 1.5,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "white" }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
        </Menu>
      )}

      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: "15.625rem",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default AppBar;
